import { useEffect } from "react";
import { trackOutgoingLink } from "./trackingService";

export default function OutgoingLinksTracker() {
    // This component is responsible for tracking outgoing links
    // It can be used to monitor user interactions with external links
    // and send data to analytics services
    useEffect(() => {
        document.addEventListener("click", handleLinkClick);
        document.addEventListener("contextmenu", handleLinkClick);
        document.addEventListener("mouseup", handleMouseUp);
        return () => {
            document.removeEventListener("click", handleLinkClick);
            document.removeEventListener("contextmenu", handleLinkClick);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, []);

    return null;
}

const handleLinkClick = (event: MouseEvent) => {
    const target = event.target as HTMLAnchorElement;
    const anchor = getAnchorElement(target);
    if (!anchor) {
        return; // No external link found
    }
    const linkType = getLinkType(anchor);
    if (linkType === "other") {
        return; // Not an external link or download
    }
    trackOutgoingLink({ linkType, href: purify(anchor.href) });
};
const handleMouseUp = (event: MouseEvent) => {
    if (event.button === 1) {
        handleLinkClick(event); // Middle mouse button click
    }
};
// Traverses the DOM to find the anchor element
function getAnchorElement(element: HTMLElement | null): HTMLAnchorElement | null {
    while (element) {
        if (element.tagName.toLowerCase() === "a" && element.hasAttribute("href")) {
            return element as HTMLAnchorElement;
        }
        element = element.parentElement;
    }
    return null;
}

function getLinkType(anchor: HTMLAnchorElement): "link" | "download" | "other" {
    try {
        const url = new URL(anchor.href, window.location.href);
        const pathname = url.pathname;
        // Define file extensions
        const fileExtensionRegex = /\.(3mf|7z|aac|apk|arc|arj|asc|asf|asx|avi|azw3|bin|bz|bz2|csv|deb|dmg|docx?|epub|exe|flv|gif|gz|gzip|hqx|ibooks|jar|jpe?g|js|md5|mobi|mov|movie|mp2|mp3|mp4|mpe?g|msi|msp|obj|odb|odf|odg|ods|odt|ogg|ogv|pdf|phps|png|ply|pptx?|qt|qtm|ra|ram|rar|rpm|rtf|sea|sha|sha256|sha512|sig|sit|stl|tar|tbz2?|tgz|torrent|txt|wav|wma|wmv|wpd|xls|xlsx|xml|xz|z|zip)$/i;

        if (fileExtensionRegex.test(pathname)) {
            // If the link ends with a file extension, treat it as a download
            return "download";
        }

        if (isExternalLink(anchor.href)) {
            // If the link is external, treat it as a link
            return "link";
        }
        return "other"; // Default case for internal links without file extensions
    } catch (e) {
        return "other"; // Return "other" if there's an error processing the URL
    }
}

function isExternalLink(href: string): boolean {
    if (!href) {
        return false;
    }
    try {
        const linkUrl = new URL(href, window.location.href);
        return linkUrl.hostname !== window.location.hostname;
    } catch (e) {
        // If the URL is invalid, treat it as internal
        return false;
    }
}

function purify(href: string): string {
    try {
        const url = new URL(href, window.location.origin); // Handles relative URLs
        const params = url.searchParams;
        // Parameters to remove based on the original code
        const toRemove = ["ignore_referrer", "ignore_referer", "pk_vid"];
        toRemove.forEach(param => params.delete(param));
        // Rebuild the URL with updated parameters
        url.search = params.toString();
        return url.toString();
    } catch (e) {
        console.error("Invalid URL:", href, e);
        return href; // Return the original href if it cannot be parsed
    }
}
